import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { Select } from '@trello/nachos/select';
import { Spinner } from '@trello/nachos/spinner';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { Alerts } from 'app/scripts/views/lib/Alerts';
import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { InputErrors } from './InputErrors';
import { usePluginBasicInformationQuery } from './PluginBasicInformationQuery.generated';
import { usePluginManagementCategoriesQuery } from './PluginManagementCategoriesQuery.generated';
import {
  isEmailOrUrl,
  isEmptyOr,
  isStringInRange,
  isValidHttpsUrl,
} from './pluginValidators';
import { useUpdatePluginBasicInformationMutation } from './UpdatePluginBasicInformationMutation.generated';
import { useValidatedSyncedState } from './useValidatedState';

import styles from './PowerUpBasicInformationPage.less';

const format = forTemplate('app_management');
const formatDirectory = forTemplate('directory');

interface SelectOption {
  value: string;
  label: string;
}

function categoryToSelectOption(category: string) {
  return {
    value: category,
    label: formatDirectory(category).replace(/&amp;/g, '&'),
  };
}

export const PowerUpBasicInformationPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const { data, error, loading } = usePluginBasicInformationQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  const { data: categoriesQuery, loading: loadingCategories } =
    usePluginManagementCategoriesQuery();

  const [icon, setIcon, iconErrors] = useValidatedSyncedState(
    data?.plugin?.icon?.url || '',
    '',
    isEmptyOr(isValidHttpsUrl(format('error-url-must-be-served-over-https'))),
  );
  const [author, setAuthor, authorErrors] = useValidatedSyncedState(
    data?.plugin?.author || '',
    '',
    isStringInRange(0, 50, format('error-author-length', { max: '50' })),
  );
  const [email, setEmail, emailErrors] = useValidatedSyncedState(
    data?.plugin?.email || '',
    '',
    isEmptyOr(isEmailOrUrl(format('error-invalid-email'))),
  );
  const [supportEmail, setSupportEmail, supportEmailErrors] =
    useValidatedSyncedState(
      data?.plugin?.supportEmail || '',
      '',
      isEmptyOr(isEmailOrUrl(format('error-invalid-email-or-url'))),
    );
  const [iframeConnectorUrl, setIframeConnectorUrl, iframeConnectorUrlErrors] =
    useValidatedSyncedState(
      data?.plugin?.iframeConnectorUrl || '',
      '',
      isValidHttpsUrl(format('error-url-must-be-served-over-https')),
    );

  const isAppKey = !data?.plugin?.iframeConnectorUrl;

  const [primaryCategory, setPrimaryCategory] = useState<string | null>(null);
  const [secondaryCategory, setSecondaryCategory] = useState<string | null>(
    null,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pluginCategories = data?.plugin?.categories || [];

  useEffect(() => {
    setPrimaryCategory(pluginCategories[0] ? pluginCategories[0] : null);

    setSecondaryCategory(pluginCategories[1] ? pluginCategories[1] : null);
  }, [pluginCategories]);

  const [updatePluginBasicInformation, { loading: updating }] =
    useUpdatePluginBasicInformationMutation();

  const updatePlugin = useCallback(async () => {
    const newCategories = [];
    const traceId = Analytics.startTask({
      taskName: 'edit-plugin/basicInformation',
      source: 'powerUpAdminBasicInfoScreen',
      attributes: {
        idPlugin,
      },
    });

    if (primaryCategory) {
      newCategories.push(primaryCategory);
    }

    if (secondaryCategory) {
      newCategories.push(secondaryCategory);
    }

    try {
      await updatePluginBasicInformation({
        variables: {
          idPlugin,
          icon,
          author,
          email,
          supportEmail,
          iframeConnectorUrl,
          categories: newCategories,
        },
      });
      Analytics.taskSucceeded({
        taskName: 'edit-plugin/basicInformation',
        traceId,
        source: 'powerUpAdminBasicInfoScreen',
        attributes: {
          idPlugin,
        },
      });
      Alerts.show('changes-saved', 'info', 'changessaved', 3000);
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-plugin/basicInformation',
        traceId,
        source: 'powerUpAdminBasicInfoScreen',
        attributes: {
          idPlugin,
        },
        error,
      });
      Alerts.show('something-went-wrong', 'error', 4000);
    }
  }, [
    updatePluginBasicInformation,
    idPlugin,
    icon,
    author,
    email,
    supportEmail,
    iframeConnectorUrl,
    primaryCategory,
    secondaryCategory,
  ]);

  const selectPrimaryCategoryOnChange = useCallback(
    ({ value }: SelectOption) => setPrimaryCategory(value),
    [setPrimaryCategory],
  );

  const selectSecondaryCategoryOnChange = useCallback(
    ({ value }: SelectOption) => setSecondaryCategory(value),
    [setSecondaryCategory],
  );

  if (loading || loadingCategories) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>{format('something-went-wrong')}</div>;
  }

  const categories = categoriesQuery?.pluginCategories
    ? categoriesQuery.pluginCategories
    : [];
  const primaryCategoryOptions = categories
    .filter((c) => c !== secondaryCategory)
    .map(categoryToSelectOption);
  const secondaryCategoryOptions = categories
    .filter((c) => c !== primaryCategory)
    .map(categoryToSelectOption);

  const canUpdate =
    !updating &&
    (isAppKey ? true : !iframeConnectorUrlErrors) &&
    !iconErrors &&
    !emailErrors &&
    !supportEmailErrors &&
    !authorErrors &&
    author.length &&
    email.length &&
    supportEmail.length;

  return (
    <>
      <div className={styles.header}>
        <h1>{format('basic-information')}</h1>
      </div>
      <div className={styles.formSection}>
        <label>
          {format('iframe-connector-url-flagged')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error':
                iframeConnectorUrl.length && iframeConnectorUrlErrors,
            })}
            name="iframe-connector-url"
            placeholder="e.g. https://my-power-up.com"
            value={iframeConnectorUrl}
            onChange={(e) => setIframeConnectorUrl(e.target.value)}
            disabled={updating || isAppKey}
            data-testid={getTestId<AppManagementTestIds>(
              'power-up-basic-information-connectorUrl-field',
            )}
          />
          {iframeConnectorUrl.length ? (
            <InputErrors errors={iframeConnectorUrlErrors} />
          ) : null}
        </label>
        <div
          className={styles.inputDescription}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: format('iframe-connector-url-description-flagged'),
          }}
        />
      </div>
      <div className={styles.formSection}>
        <label>
          {format('icon')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': iconErrors,
            })}
            name="icon"
            placeholder="e.g. https://my-power-up.com/icon.png"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            disabled={updating}
            data-testid={getTestId<AppManagementTestIds>(
              'power-up-basic-information-icon-field',
            )}
          />
          <InputErrors errors={iconErrors} />
        </label>
        <div
          className={styles.inputDescription}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: format('icon-description'),
          }}
        />
      </div>
      <div className={styles.formSection}>
        <div>
          <label>{format('categories')}</label>
          <Select
            blurInputOnSelect={true}
            className={styles.pluginInput}
            options={primaryCategoryOptions}
            placeholder={format('primary')}
            isDisabled={isAppKey}
            value={
              primaryCategory ? categoryToSelectOption(primaryCategory) : null
            }
            // @ts-expect-error
            onChange={selectPrimaryCategoryOnChange}
          />
          <Select
            blurInputOnSelect={true}
            className={styles.pluginInput}
            options={secondaryCategoryOptions}
            placeholder={format('secondary')}
            isDisabled={isAppKey}
            value={
              secondaryCategory
                ? categoryToSelectOption(secondaryCategory)
                : null
            }
            // @ts-expect-error
            onChange={selectSecondaryCategoryOnChange}
          />
        </div>
        <div className={styles.inputDescription}>
          {format('categories-description')}
        </div>
      </div>
      <div className={styles.formSection}>
        <label>
          {format('email')}
          <input
            type="email"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': emailErrors,
            })}
            name="email"
            placeholder="me@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={updating}
            data-testid={getTestId<AppManagementTestIds>(
              'power-up-basic-information-email-field',
            )}
          />
          <InputErrors errors={emailErrors} />
        </label>
        <div className={styles.inputDescription}>
          {format('email-description')}
        </div>
      </div>
      <div className={styles.formSection}>
        <label>
          {format('support-email')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': supportEmailErrors,
            })}
            name="support-email"
            placeholder="support@company.com"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            disabled={updating}
          />
          <InputErrors errors={supportEmailErrors} />
        </label>
        <div className={styles.inputDescription}>
          {format('support-email-description')}
        </div>
      </div>
      <div className={styles.formSection}>
        <label>
          {format('author')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': authorErrors,
            })}
            name="author"
            placeholder="Author Name"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            disabled={updating}
            data-testid={getTestId<AppManagementTestIds>(
              'power-up-basic-information-author-field',
            )}
          />
          <InputErrors errors={authorErrors} />
        </label>
        <div className={styles.inputDescription}>
          {format('author-description')}
        </div>
      </div>
      <div className={classNames(styles.footer, styles.formFooter)}>
        <Button
          appearance="primary"
          onClick={updatePlugin}
          isDisabled={!canUpdate}
        >
          {updating ? <Spinner small /> : format('save')}
        </Button>
      </div>
    </>
  );
};
